import React from 'react';
import PropTypes from 'prop-types';
import { kebabCase } from 'lodash';
import { Link } from 'react-router-dom';

import { useClassName } from 'common/hooks';
import { track } from 'utils/analytics';
import { scrollTo } from 'utils/helpers/scroll';

import {
  getCtaType,
  withOpacity,
  normalizeColor,
  DEFAULT_CTA_OPACITY,
} from './utils';

const DEFAULT_FONT_COLOR = '#FFFFFF';
const DEFAULT_BACKGROUND_COLOR = '#FFFFFF';

const JUMP_OFFSET = -64;

function jumpToElement(url) {
  const id = String(url || '').replace('#', '');
  const element =
    document.getElementById(id) || document.getElementById(kebabCase(id));

  if (element) scrollTo(element, JUMP_OFFSET);
}

export default function ContentBannerCTA({ cta, style, onClick }) {
  const classNames = useClassName('ContentBanner');

  if (!cta?.label) return null;

  const {
    label,
    url,
    backgroundColor,
    backgroundHoverColor,
    fontColor,
    fontHoverColor,
    opacity = DEFAULT_CTA_OPACITY,
  } = cta;

  const type = getCtaType(cta.type);
  const color = normalizeColor(fontColor, DEFAULT_FONT_COLOR);
  const background = normalizeColor(backgroundColor, DEFAULT_BACKGROUND_COLOR);

  const attrs = {
    className: classNames('cta'),
    style: {
      background: withOpacity(background, opacity),
      color,
      '--cta-hover-background': withOpacity(
        backgroundHoverColor || background,
        opacity
      ),
      '--cta-hover-color': normalizeColor(fontHoverColor, color),
      ...style,
    },
  };

  const handleClick = (event) => {
    track('Banner CTA pressed', {
      label,
      url,
      type,
      category: type,
    });

    if (type === 'jump') {
      event.preventDefault();
      jumpToElement(url);
    }

    onClick?.(event, cta);
  };

  if (type === 'external') {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        {...attrs}>
        {label}
      </a>
    );
  }

  if (type === 'jump') {
    return (
      <a
        href={`#${String(url || '').replace('#', '')}`}
        onClick={handleClick}
        {...attrs}>
        {label}
      </a>
    );
  }

  return (
    <Link to={url || ''} onClick={handleClick} {...attrs}>
      {label}
    </Link>
  );
}

ContentBannerCTA.ctaShape = {
  id: PropTypes.string,
  label: PropTypes.string,
  type: PropTypes.string,
  url: PropTypes.string,
  backgroundColor: PropTypes.string,
  backgroundHoverColor: PropTypes.string,
  fontColor: PropTypes.string,
  fontHoverColor: PropTypes.string,
  opacity: PropTypes.number,
};

ContentBannerCTA.propTypes = {
  cta: PropTypes.shape(ContentBannerCTA.ctaShape),
  style: PropTypes.object,
  onClick: PropTypes.func,
};
